import React from 'react';
import { ToggleButton, ToggleButtonGroup } from 'react-aria-components';

import { tv } from '../../tv';
import { Size } from '../../types';
import { buttonStyles } from '../button/button';
import { TooltipSeat } from '../tooltip/tooltip';

/**
 * ComplyKit SegmentedControl: one choice from a small, closed set, drawn as
 * a recessed track with the chosen segment raised on it. A view switch (grid
 * or list), a filter mode (all or unanswered), a version type.
 *
 * Exactly one segment is always selected: the control answers "which one",
 * never "whether". Something that can be switched off is a Switch or a
 * Checkbox, and content that changes with the choice is Tabs.
 *
 * Built on React Aria's ToggleButtonGroup in single selection, so the bar is
 * a radiogroup, each segment a radio, and the arrow keys walk the segments.
 * Geometry comes from Button's own recipe (a segment is a ghost button of the
 * ramp), so the two can never drift; the track and the chip are the
 * control's own, and there is no border anywhere in it. One of `value` or
 * `defaultValue` is required: a segment is always selected.
 */
interface SegmentedControlCommonProps {
  /**
   * The group's accessible name: what is being chosen ("View", "Show").
   * A bar of segments has no visible caption to name it.
   */
  label: string;
  /** Fires with the newly selected segment's id. Never fires with nothing selected. */
  onChange?: (value: string) => void;
  /** The Button ramp: sm 24px, md 32px, lg 40px; md is the default. */
  size?: Size;
  /** Whether the whole control is disabled. */
  isDisabled?: boolean;
  /** The segments: `SegmentedControlItem` elements. */
  children: React.ReactNode;
  id?: string;
}

interface SegmentedControlControlledProps extends SegmentedControlCommonProps {
  /** The selected segment's id (controlled). */
  value: string;
  defaultValue?: never;
}

interface SegmentedControlUncontrolledProps extends SegmentedControlCommonProps {
  /** The initially selected segment's id (uncontrolled). */
  defaultValue: string;
  value?: never;
}

export type SegmentedControlProps =
  | SegmentedControlControlledProps
  | SegmentedControlUncontrolledProps;

interface SegmentedControlItemCommonProps {
  /** The segment's id: what `value` and `onChange` speak in. */
  id: string;
  /** Whether this segment alone is disabled. */
  isDisabled?: boolean;
}

interface SegmentedControlTextItemProps extends SegmentedControlItemCommonProps {
  /** The segment's text. */
  children: string;
  /**
   * A glyph before the text: a heroicons 24/outline component, sized and
   * coloured by the segment (icon.md).
   */
  iconStart?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  icon?: never;
  label?: never;
}

interface SegmentedControlIconItemProps extends SegmentedControlItemCommonProps {
  /** The glyph of an icon-only segment, the square of the ramp. */
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  /**
   * The icon-only segment's name, "Grid view" not "Squares". It is the
   * accessible name and shows as an instant tooltip, the icon-only standard
   * (tooltip.md), exactly as IconButton pairs its label.
   */
  label: string;
  children?: never;
  iconStart?: never;
}

export type SegmentedControlItemProps =
  | SegmentedControlTextItemProps
  | SegmentedControlIconItemProps;

const SizeContext = React.createContext<Size>('md');

/* The track: the neutral wash recessed one pixel around the segments, on the
 * ramp's heights, the same anatomy Radix Themes, Spectrum and Primer draw.
 * The segments stretch to fill it. */
const trackStyles = tv({
  base: 'ck:inline-flex ck:items-stretch ck:rounded-3 ck:bg-neutral-a-3 ck:p-px',
  variants: { size: { sm: 'ck:h-6', md: 'ck:h-8', lg: 'ck:h-10' } },
});

/* A segment is a ghost button of the ramp, extending Button's recipe rather
 * than restating it: text sizes, padding and glyph sizes are Button's by
 * construction. Its states ride React Aria's data-selected through the
 * plugin variant, like every other control's. */
const segmentStyles = tv({ extend: buttonStyles });

/* What the segment adds to the ghost button, applied as the call-time class
 * so it outranks Button's own neutral pairings: it fills the track's height
 * and takes the inner radius; unselected segments rest muted with no wash,
 * pointing at one only lifts its text; the chosen segment is the raised chip,
 * the surface with Card's subtle edge and the small shadow, and stays so
 * under hover and press. */
const segmentState = [
  'ck:h-auto ck:rounded-2 ck:text-text-muted',
  'ck:hover:bg-transparent ck:pressed:bg-transparent ck:hover:text-text-default',
  'ck:selected:text-text-default ck:selected:bg-surface-raised',
  'ck:selected:border-border-subtle ck:selected:shadow-sm',
  'ck:selected:hover:bg-surface-raised ck:selected:pressed:bg-surface-raised',
].join(' ');
const squareSegment = 'ck:w-auto ck:aspect-square';

export const SegmentedControl = React.forwardRef<HTMLDivElement, SegmentedControlProps>(
  function SegmentedControl(
    { label, value, defaultValue, onChange, size = 'md', isDisabled = false, children, ...props },
    ref,
  ) {
    // React Aria reports a press on the chosen segment as a selection change
    // to the same key; the choice did not move, so nothing is reported.
    const chosen = React.useRef(value ?? defaultValue);
    return (
      <SizeContext.Provider value={size}>
        <ToggleButtonGroup
          {...props}
          ref={ref}
          aria-label={label}
          selectionMode="single"
          disallowEmptySelection
          isDisabled={isDisabled}
          selectedKeys={value === undefined ? undefined : [value]}
          defaultSelectedKeys={defaultValue === undefined ? undefined : [defaultValue]}
          onSelectionChange={(keys) => {
            const [next] = keys;
            if (next === undefined || String(next) === (value ?? chosen.current)) return;
            chosen.current = String(next);
            onChange?.(String(next));
          }}
          className={trackStyles({ size })}
          data-component="segmented-control"
        >
          {children}
        </ToggleButtonGroup>
      </SizeContext.Provider>
    );
  },
);

export const SegmentedControlItem = React.forwardRef<HTMLButtonElement, SegmentedControlItemProps>(
  function SegmentedControlItem({ id, isDisabled = false, ...props }, ref) {
    const size = React.useContext(SizeContext);
    const iconOnly = props.icon !== undefined;
    const Glyph = iconOnly ? props.icon : props.iconStart;
    const { root, content, icon } = segmentStyles({
      variant: 'ghost',
      tone: 'neutral',
      size,
      shape: iconOnly ? 'square' : 'label',
      pending: false,
      ariaDisabled: false,
    });

    const element = (
      <ToggleButton
        id={id}
        ref={ref}
        isDisabled={isDisabled}
        aria-label={iconOnly ? props.label : undefined}
        className={root({ class: iconOnly ? `${segmentState} ${squareSegment}` : segmentState })}
        data-component="segmented-control-item"
        data-tone="neutral"
      >
        <span className={content()}>
          {Glyph && <Glyph aria-hidden="true" focusable="false" className={icon()} />}
          {!iconOnly && props.children}
        </span>
      </ToggleButton>
    );

    if (!iconOnly) return element;

    // The icon-only pairing: the name one hover away, no delay (tooltip.md).
    return (
      <TooltipSeat content={props.label} placement="top" delay={0}>
        {element}
      </TooltipSeat>
    );
  },
);
